import config from '@medflex/utils/config';
import { format } from 'date-fns';
import { TaConfigFileTypes, TaScheduleTypes } from '@medflex/shared-types';
import sortBy from 'lodash/sortBy';
import makeApiRequest from '../../../api/makeApiRequest';
import { OUTPUT_TYPES } from '../../modules/PhysicianAdministrationModule/InquiryManagement/PhoneOpeningHours/PhoneOpeningHoursEdit';

const getFileIdFromUri = uri => uri.split('/').pop();

const getFileIdFromFiles = files => {
  if (!files || !Array.isArray(files) || files.length === 0) throw new Error('Invalid files array');
  const file = files[0];
  if (file.uploadURL) {
    return getFileIdFromUri(file.uploadURL);
  }
  if (file.response?.body?.url) {
    return getFileIdFromUri(file.response.body.url);
  }
  return file.id;
};

const formatWeekdaysSlotsTimeToDate = weekdays => {
  return weekdays.map((weekday, idx) => {
    const { slots, ...rest } = weekday;
    const day = idx === 6 ? 0 : idx + 1;
    const formattedSlots =
      slots?.map(slot => ({
        startsAt: new Date(`1970-01-01T${slot.startTime}`).toISOString() || '',
        endsAt: new Date(`1970-01-01T${slot.endTime}`).toISOString() || '',
      })) || [];

    return {
      ...rest,
      day,
      slots: sortBy(formattedSlots, [slot => slot.startsAt]),
    };
  });
};

const formatWeekdaysSlotsDatestring = weekdays => {
  return weekdays.map(({ active, slots }) => {
    const formattedSlots = slots.map(({ startsAt, endsAt }) => {
      return {
        startTime: format(new Date(startsAt), 'HH:mm'),
        endTime: format(new Date(endsAt), 'HH:mm'),
      };
    });
    return { active, slots: formattedSlots };
  });
};

const getOutputType = files => {
  if (!files || !Array.isArray(files) || files.length === 0) return '';

  return files.some(file => file.type === TaConfigFileTypes.TEXT_TO_SPEECH)
    ? OUTPUT_TYPES.TEXT_TO_SPEECH
    : OUTPUT_TYPES.AUDIO_FILE_UPLOAD;
};

const getSpokenText = files => {
  if (!files || !Array.isArray(files) || files.length === 0) return '';
  const textToSpeechFile = files.find(file => file.type === TaConfigFileTypes.TEXT_TO_SPEECH);

  return textToSpeechFile?.text || '';
};

const processDataFromService = schedules => {
  return schedules.map(schedule => {
    if (schedule.type === TaScheduleTypes.OPENING_HOURS) {
      const { weekdays, files, ...rest } = schedule;
      const formattedWeekdays = formatWeekdaysSlotsDatestring(weekdays);
      const outputType = getOutputType(schedule.files);

      return {
        ...rest,
        weekdays: formattedWeekdays,
        files: outputType === OUTPUT_TYPES.TEXT_TO_SPEECH ? [] : files,
        outputType,
        ...(outputType === OUTPUT_TYPES.TEXT_TO_SPEECH
          ? { textToSpeech: getSpokenText(schedule.files) }
          : {}),
      };
    }

    return schedule;
  });
};

const TaConfigSchedules = {
  name: 'taConfigSchedules',
  state: {
    taConfigSchedules: [],
  },
  reducers: {
    onLoaded(state, schedules) {
      return { ...state, taConfigSchedules: schedules };
    },
    onCreated(state, schedule) {
      return {
        ...state,
        taConfigSchedules: [...state.taConfigSchedules, schedule],
      };
    },
    onUpdated(state, schedule) {
      const taConfigSchedules = state.taConfigSchedules.map(item =>
        item.type === schedule.type ? schedule : item,
      );
      return { ...state, taConfigSchedules };
    },
  },
  effects: dispatch => ({
    async load(filter, { oidcSession }) {
      const url = new URL(`${config.TA_API_ENDPOINT}/schedules`);
      url.search = new URLSearchParams(filter);
      const [request] = makeApiRequest({ url, method: 'get' }, oidcSession);
      const { error, data } = await request;
      if (error) {
        dispatch.errors.add({ realm: 'taConfigSchedules', type: 'load', error });
        return;
      }
      if (data) {
        dispatch.errors.remove({ realm: 'taConfigSchedules', type: 'load' });
        const processedSchedules = processDataFromService(data);
        this.onLoaded(processedSchedules);
      }
    },
    async create(payload, { oidcSession }) {
      dispatch.errors.remove({ realm: 'taConfigSchedules', type: 'create' });
      const url = new URL(`${config.TA_API_ENDPOINT}/schedules`);
      const { weekdays, files, outputType, textToSpeech, ...rest } = payload;
      const formattedWeekdays = formatWeekdaysSlotsTimeToDate(weekdays);
      const requestConfig = {
        url,
        method: 'post',
        data: {
          ...rest,
          weekdays: formattedWeekdays,
          ...(outputType === OUTPUT_TYPES.TEXT_TO_SPEECH
            ? { textToSpeech }
            : { fileId: getFileIdFromFiles(files) }),
        },
      };
      const [request] = makeApiRequest(requestConfig, oidcSession);
      const { error, data } = await request;
      if (error) {
        dispatch.errors.add({ realm: 'taConfigSchedules', type: 'create', error });
        throw error;
      }
      const processedSchedules = processDataFromService([data]);
      this.onCreated(...processedSchedules);
    },
    async edit(payload, { oidcSession }) {
      dispatch.errors.remove({ realm: 'taConfigSchedules', type: 'update' });
      const url = new URL(`${config.TA_API_ENDPOINT}/schedules`);
      const { weekdays, files, outputType, textToSpeech, ...rest } = payload;
      const formattedWeekdays = formatWeekdaysSlotsTimeToDate(weekdays);
      const requestConfig = {
        url,
        method: 'patch',
        data: {
          ...rest,
          weekdays: formattedWeekdays,
          ...(outputType === OUTPUT_TYPES.TEXT_TO_SPEECH
            ? { textToSpeech }
            : { fileId: getFileIdFromFiles(files) }),
        },
      };
      const [request] = makeApiRequest(requestConfig, oidcSession);
      const { error, data } = await request;
      if (error) {
        dispatch.errors.add({ realm: 'taConfigSchedules', type: 'update', error });
        throw error;
      }
      const processedSchedules = processDataFromService([data]);
      this.onUpdated(...processedSchedules);
    },
  }),
  selectors: slice => ({
    byType() {
      const result = slice(({ taConfigSchedules: schedules }) => {
        if (!schedules || !Array.isArray(schedules)) return {};
        return Object.groupBy(schedules, ({ type }) => type);
      });
      return result || {};
    },
  }),
};

export default TaConfigSchedules;
